import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2, CreditCard, StopCircle, RotateCcw } from 'lucide-react';
import api from '../utils/axiosConfig';

function formatDate(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

const CURRENCY_SYMBOLS = { NOK: 'kr', USD: '$', EUR: '€' };

function formatPrice(cents, currency) {
  const sym = CURRENCY_SYMBOLS[currency?.toUpperCase()] || '$';
  const amount = cents / 100;
  return sym === '$' || sym === '€' ? `${sym}${amount.toFixed(2)}` : `${amount.toFixed(0)} ${sym}`;
}

const ROLE_LABELS = {
  athlete: 'Athlete',
  coach: 'Coach',
  podcast_manager: 'Podcast Manager',
};

const PlanUpgradeModal = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const [userPlans, setUserPlans] = useState({});
  const [availableByRole, setAvailableByRole] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [cancelConfirmRole, setCancelConfirmRole] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCheckoutError(null);
    const fetch = async () => {
      try {
        setLoading(true);
        const [meRes, athleteRes, coachRes, podcastRes] = await Promise.all([
          api.get('/api/plans/me'),
          api.get('/api/plans/available?role=athlete'),
          api.get('/api/plans/available?role=coach'),
          api.get('/api/plans/available?role=podcast_manager'),
        ]);
        setUserPlans(meRes.data || {});
        setAvailableByRole({
          athlete: athleteRes.data?.plans || [],
          coach: coachRes.data?.plans || [],
          podcast_manager: podcastRes.data?.plans || [],
        });
      } catch (err) {
        console.error('Failed to fetch plans:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [open]);

  const handleCancelSubscription = async (roleType) => {
    try {
      setActionLoading(`cancel-${roleType}`);
      await api.post('/api/stripe/subscription/cancel', { roleType });
      setUserPlans((prev) => ({
        ...prev,
        [roleType]: { ...prev[roleType], cancelAtPeriodEnd: true },
      }));
      setCancelConfirmRole(null);
      window.dispatchEvent(new Event('planSubscriptionUpdated'));
    } catch (err) {
      setCheckoutError(err.response?.data?.error || err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (roleType) => {
    try {
      setActionLoading(`reactivate-${roleType}`);
      await api.post('/api/stripe/subscription/reactivate', { roleType });
      setUserPlans((prev) => ({
        ...prev,
        [roleType]: { ...prev[roleType], cancelAtPeriodEnd: false },
      }));
      window.dispatchEvent(new Event('planSubscriptionUpdated'));
    } catch (err) {
      setCheckoutError(err.response?.data?.error || err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpgrade = async (priceId) => {
    if (!priceId) {
      setCheckoutError(t('plans.priceNotConfigured') || 'Price not configured for your region. Please contact support.');
      return;
    }
    setCheckoutError(null);
    try {
      setCheckoutLoading(priceId);
      const res = await api.post('/api/stripe/checkout/session', {
        priceId,
        quantity: 1,
        mode: 'subscription',
      });
      const url = res.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setCheckoutError(t('plans.checkoutError') || 'Checkout could not be started. Please try again.');
        setCheckoutLoading(null);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Checkout failed. Please try again.';
      setCheckoutError(msg);
      setCheckoutLoading(null);
    }
  };

  const userRoles = Object.keys(userPlans).filter((r) => userPlans[r]);
  const activeTab = userRoles[0] || 'athlete';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[60vw] max-h-[80vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{t('plans.title')}</DialogTitle>
          <DialogDescription>{t('plans.description')}</DialogDescription>
        </DialogHeader>

        {checkoutError && (
          <div className='rounded-md bg-destructive/10 text-destructive text-sm p-3 flex items-center justify-between gap-2'>
            <span>{checkoutError}</span>
            <button
              type='button'
              onClick={() => setCheckoutError(null)}
              className='text-destructive hover:underline shrink-0'
            >
              {t('common.close')}
            </button>
          </div>
        )}
        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <Loader2 className='h-8 w-8 animate-spin text-primary' />
          </div>
        ) : (
          <Tabs defaultValue={activeTab} className='w-full'>
            <TabsList>
              {userRoles.map((role) => (
                <TabsTrigger key={role} value={role}>
                  {ROLE_LABELS[role] || role}
                </TabsTrigger>
              ))}
            </TabsList>
            {userRoles.map((role) => (
              <TabsContent key={role} value={role} className='space-y-4'>
                <div className='text-sm text-muted-foreground space-y-1'>
                  <div>
                    {t('plans.currentPlan')}:{' '}
                    <Badge variant='secondary'>{userPlans[role]?.name || '—'}</Badge>
                  </div>
                  {userPlans[role]?.currentPeriodEnd && userPlans[role]?.status === 'active' && (
                    <div className='text-xs'>
                      {userPlans[role].cancelAtPeriodEnd
                        ? t('plans.cancelsOn', { date: formatDate(userPlans[role].currentPeriodEnd) || '—' })
                        : t('plans.renewsOn', { date: formatDate(userPlans[role].currentPeriodEnd) || '—' })}
                    </div>
                  )}
                </div>
                {userPlans[role]?.status === 'active' && userPlans[role]?.stripeSubscriptionId && (
                  <div className='flex gap-2'>
                    {userPlans[role].cancelAtPeriodEnd ? (
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => handleReactivate(role)}
                        disabled={!!actionLoading}
                      >
                        {actionLoading === `reactivate-${role}` ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <>
                            <RotateCcw className='h-4 w-4 mr-1' />
                            {t('plans.continueSubscription')}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='text-amber-600 hover:text-amber-700'
                        onClick={() => setCancelConfirmRole(role)}
                        disabled={!!actionLoading}
                      >
                        <StopCircle className='h-4 w-4 mr-1' />
                        {t('plans.stopSubscription')}
                      </Button>
                    )}
                  </div>
                )}
                <div className='grid gap-3'>
                  {(availableByRole[role] || []).map((plan) => {
                    const isCurrent = userPlans[role]?.planKey === plan.plan_key;
                    const isPaid = plan.amount_cents > 0 && plan.stripe_price_id;
                    return (
                      <div
                        key={plan.id}
                        className='flex items-center justify-between p-3 rounded-lg border border-border'
                      >
                        <div>
                          <p className='font-medium'>{plan.name}</p>
                          {plan.description && (
                            <p className='text-xs text-muted-foreground'>{plan.description}</p>
                          )}
                        </div>
                        <div className='flex items-center gap-2'>
                          {plan.amount_cents > 0 ? (
                            <span className='text-sm font-medium'>
                              {formatPrice(plan.amount_cents, plan.currency)}
                              {plan.interval === 'month' ? '/mo' : plan.interval === 'year' ? '/yr' : ''}
                            </span>
                          ) : (
                            <Badge variant='outline'>{t('plans.free')}</Badge>
                          )}
                          {isCurrent ? (
                            <Badge>{t('plans.current')}</Badge>
                          ) : isPaid ? (
                            <Button
                              type='button'
                              size='sm'
                              onClick={() => handleUpgrade(plan.stripe_price_id)}
                              disabled={!!checkoutLoading}
                            >
                              {checkoutLoading === plan.stripe_price_id ? (
                                <Loader2 className='h-4 w-4 animate-spin' />
                              ) : (
                                <>
                                  <CreditCard className='h-4 w-4 mr-1' />
                                  {t('plans.upgrade')}
                                </>
                              )}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </DialogContent>

      <Dialog open={!!cancelConfirmRole} onOpenChange={(o) => !o && setCancelConfirmRole(null)}>
        <DialogContent showCloseButton={false} className='max-w-[90vw] w-[400px]'>
          <DialogHeader>
            <DialogTitle>{t('plans.cancelConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('plans.cancelConfirmDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className='gap-2 sm:gap-0'>
            <Button variant='outline' onClick={() => setCancelConfirmRole(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant='destructive'
              onClick={() => cancelConfirmRole && !actionLoading && handleCancelSubscription(cancelConfirmRole)}
              disabled={!!actionLoading}
            >
              {actionLoading?.startsWith('cancel-') ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                t('plans.confirmStop')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default PlanUpgradeModal;
